import { Edge, Node } from 'reactflow'
import { IWorkflowAddPage } from 'types/workflows.interface'

export type WorkflowItem = IWorkflowAddPage['items'][number]
export type LinkType = 1 | 2

export type TaskNodeData = {
  taskTemplateId: number
  name: string
  isRoot?: boolean
}

const NODE_WIDTH = 180
const NODE_HEIGHT = 80
const COLUMN_GAP = 260
const ROW_GAP = 140

export const nodeIdFor = (level: number, index: number) =>
  `level-${level}_index-${index}`

const parseNodeId = (id: string) => {
  const [, level, , index] = id.split(/[-_]/)
  return { level: Number(level), index: Number(index) }
}

export const itemsToGraph = (items: WorkflowItem[]): { nodes: Node<TaskNodeData>[]; edges: Edge[] } => {
  const levelCounts: Record<number, number> = {}
  for (const it of items) {
    levelCounts[it.level] = Math.max(levelCounts[it.level] ?? 0, it.index + 1)
  }

  const nodes: Node<TaskNodeData>[] = items.map((it) => ({
    id: nodeIdFor(it.level, it.index),
    type: 'task',
    position: {
      x: it.level * COLUMN_GAP,
      y: it.index * ROW_GAP,
    },
    data: { taskTemplateId: it.taskTemplateId, name: it.name },
  }))

  const edges: Edge[] = []
  for (const it of items) {
    const target = nodeIdFor(it.level, it.index)
    for (const p of it.parentItems) {
      const source = nodeIdFor(p.level, p.index)
      edges.push({
        id: `${source}->${target}`,
        source,
        target,
        type: 'deletable',
        animated: p.linkType === 2,
        data: { linkType: p.linkType },
      })
    }
  }

  return { nodes, edges }
}

export const graphToItems = (
  nodes: Node<TaskNodeData>[],
  edges: Edge[],
): WorkflowItem[] => {
  if (nodes.length === 0) return []

  const parents: Record<string, string[]> = {}
  const children: Record<string, string[]> = {}
  for (const n of nodes) {
    parents[n.id] = []
    children[n.id] = []
  }
  for (const e of edges) {
    if (!parents[e.target] || !children[e.source]) continue
    parents[e.target].push(e.source)
    children[e.source].push(e.target)
  }

  const levels: Record<string, number> = {}
  const visiting = new Set<string>()
  const computeLevel = (id: string): number => {
    if (levels[id] !== undefined) return levels[id]
    if (visiting.has(id)) return 0
    visiting.add(id)
    const ps = parents[id]
    const lvl = ps.length === 0 ? 0 : Math.max(...ps.map(computeLevel)) + 1
    visiting.delete(id)
    levels[id] = lvl
    return lvl
  }
  nodes.forEach((n) => computeLevel(n.id))

  const nodesByLevel: Record<number, Node<TaskNodeData>[]> = {}
  for (const n of nodes) {
    const lvl = levels[n.id]
    if (!nodesByLevel[lvl]) nodesByLevel[lvl] = []
    nodesByLevel[lvl].push(n)
  }

  const indexById: Record<string, number> = {}
  Object.keys(nodesByLevel).forEach((lvlStr) => {
    const lvl = Number(lvlStr)
    nodesByLevel[lvl].sort((a, b) => a.position.y - b.position.y)
    nodesByLevel[lvl].forEach((n, i) => {
      indexById[n.id] = i
    })
  })

  const items: WorkflowItem[] = nodes.map((n) => ({
    taskTemplateId: n.data.taskTemplateId,
    name: n.data.name,
    level: levels[n.id],
    index: indexById[n.id],
    parentItems: parents[n.id].map((pid) => ({
      level: levels[pid],
      index: indexById[pid],
      linkType: (edges.find((e) => e.source === pid && e.target === n.id)?.data?.linkType ?? 1) as LinkType,
    })),
  }))

  return items
}

export const autoLayout = (nodes: Node<TaskNodeData>[], edges: Edge[]): Node<TaskNodeData>[] => {
  if (nodes.length === 0) return nodes

  const parents: Record<string, string[]> = {}
  for (const n of nodes) parents[n.id] = []
  for (const e of edges) {
    if (parents[e.target]) parents[e.target].push(e.source)
  }

  const levels: Record<string, number> = {}
  const visiting = new Set<string>()
  const computeLevel = (id: string): number => {
    if (levels[id] !== undefined) return levels[id]
    if (visiting.has(id)) return 0
    visiting.add(id)
    const ps = parents[id]
    const lvl = ps.length === 0 ? 0 : Math.max(...ps.map(computeLevel)) + 1
    visiting.delete(id)
    levels[id] = lvl
    return lvl
  }
  nodes.forEach((n) => computeLevel(n.id))

  const nodesByLevel: Record<number, Node<TaskNodeData>[]> = {}
  for (const n of nodes) {
    const lvl = levels[n.id]
    if (!nodesByLevel[lvl]) nodesByLevel[lvl] = []
    nodesByLevel[lvl].push(n)
  }

  return nodes.map((n) => {
    const lvl = levels[n.id]
    const siblings = nodesByLevel[lvl]
    siblings.sort((a, b) => a.position.y - b.position.y)
    const idx = siblings.findIndex((s) => s.id === n.id)
    return {
      ...n,
      position: {
        x: lvl * COLUMN_GAP,
        y: idx * ROW_GAP,
      },
    }
  })
}

export { NODE_WIDTH, NODE_HEIGHT, COLUMN_GAP, ROW_GAP, parseNodeId }
